import * as React from 'react';
import PropTypes from 'prop-types';
import UserDetailsForm from '../../ts-components/page-registration/user-details';
import appState from '../../state/AppStateContainer';
import Tabs from '../../components/shared/Tabs'

import './styles.css';
import { ServiceUser } from '../../services/service-user';
import AppChangePassword from './../app-password/app-change-password';
import _ from 'lodash';
import AppLayout from '../../components/layout/app-layout/app-layout';
import { useTranslation } from 'react-i18next';
import { CustomToggle } from '../../components/shared';
import AppTenderWatchPanel from '../app-tender-watch/app-tender-watch-panel';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      className="custom-tab-style"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {(value === index) && children}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

export default function Settings() {
  const [value, setValue] = React.useState(0);
  const [userData, setUserData] = React.useState(null);
  const [t] = useTranslation('profile');
  const notificationPreferences = appState.getUserNotificationPreferences();

  React.useEffect(() => {
    const fetchAndSetUser = async () => {
      const userId = appState.getUserId();
      let user = await ServiceUser.getUserById(userId);
      setUserData(user);
    };
    fetchAndSetUser();
  }, []);

  const handleChange = (newValue) => {
    setValue(newValue);
  };

  // Preferences are user-global, so product access is checked across every org
  // the user belongs to rather than just the current one. The product helpers read legacy
  // orgs with no activeProducts as PURCHASING; hasPublicTenders deliberately does not.
  const organizations = appState.getOrganizations();
  const hasSalesOrganization = organizations.some(org => appState.isSalesActive(org));
  const hasPublicTenderOrganization = organizations.some(org => appState.hasPublicTenders(org));
  const hasPurchasingOrganization = organizations.some(org => appState.isPurchasingActive(org));
  // A bevakning is per organisation and its endpoint is gated on public tenders for the org in the
  // URL, so the panel follows the selected org rather than the user-global toggles around it.
  const currentOrgHasPublicTenders = appState.hasPublicTenders(appState.getCurrentOrg());
  // The save endpoint (PUT updateUserPreferences) only admits customer roles —
  // don't show supplier/guest users toggles they can neither save nor receive.
  const isCustomerUser = appState.isCustomerUser();

  // Each toggle stays hidden unless the user belongs to an org that receives that email: the
  // digest needs public tenders (SALES or the add-on), the reminders SALES, the rest PURCHASING.
  // defaultChecked mirrors each backend's gate: weeklyDigest sends only on a
  // strict `true` (opt-in), the rest send unless `false` (opt-out).
  const notificationSections = [
    {
      key: 'weekly-digest',
      visible: hasPublicTenderOrganization,
      title: t('notifications-settings.groups.weekly-digest.title', 'Veckomail om upphandlingar'),
      description: t('notifications-settings.groups.weekly-digest.description', 'Ett mail varje onsdag med nya upphandlingar som matchar din bevakning.'),
      extra: currentOrgHasPublicTenders ? <AppTenderWatchPanel showsPrivate={appState.isSalesActive(appState.getCurrentOrg())} /> : null,
      rows: [
        // Published requests are the private tab, which only SALES opens.
        ...(hasSalesOrganization ? [{
          key: 'weeklyDigest',
          defaultChecked: false,
          title: t('notifications-settings.weekly-digest-private.title', 'Förfrågningar på marknadsplatsen'),
          description: t('notifications-settings.weekly-digest-private.description', 'Nya publicerade förfrågningar från inköpare i Accurator.'),
        }] : []),
        {
          key: 'weeklyDigestPublic',
          defaultChecked: false,
          title: t('notifications-settings.weekly-digest-public.title', 'Offentliga upphandlingar'),
          description: t('notifications-settings.weekly-digest-public.description', 'Upphandlingar från EU:s annonsdatabas TED. Slås på när du sparar en bevakning.'),
        },
      ],
    },
    {
      key: 'project-activity',
      visible: hasPurchasingOrganization,
      title: t('notifications-settings.groups.project-activity.title', 'Aktivitet i dina projekt'),
      description: t('notifications-settings.groups.project-activity.description', 'Vad leverantörer gör i de upphandlingar du driver.'),
      rows: [
        {
          key: 'bidReceived',
          defaultChecked: true,
          title: t('notifications-settings.bid-received.title', 'Nya anbud'),
          description: t('notifications-settings.bid-received.description', 'Ett mail så snart en leverantör lämnar anbud i dina projekt.'),
        },
        {
          key: 'intentReceived',
          defaultChecked: true,
          title: t('notifications-settings.intent-received.title', 'Anbudsavsikter'),
          description: t('notifications-settings.intent-received.description', 'Ett mail när en leverantör svarar om de tänker lämna anbud.'),
        },
        {
          key: 'messageDigest',
          defaultChecked: true,
          title: t('notifications-settings.message-digest.title', 'Daglig sammanfattning av meddelanden'),
          description: t('notifications-settings.message-digest.description', 'Varje morgon en sammanfattning av gårdagens olästa meddelanden från leverantörer.'),
        },
      ],
    },
    {
      key: 'reminders',
      visible: hasSalesOrganization,
      title: t('notifications-settings.groups.reminders.title', 'Påminnelser'),
      description: t('notifications-settings.groups.reminders.description', 'Påminnelser om uppgifter som gör er lättare att hitta och anlita.'),
      rows: [
        {
          key: 'supplierInsuranceReminder',
          defaultChecked: true,
          title: t('notifications-settings.supplier-insurance-reminder.title', 'Försäkring som går ut'),
          description: t('notifications-settings.supplier-insurance-reminder.description', 'Ett mail när er ansvarsförsäkring snart går ut, så att ni hinner förnya den.'),
        },
        {
          key: 'supplierProfileReminder',
          defaultChecked: true,
          title: t('notifications-settings.supplier-profile-reminder.title', 'Ofullständig leverantörsprofil'),
          description: t('notifications-settings.supplier-profile-reminder.description', 'En påminnelse när profilen saknar uppgifter som hjälper inköpare att hitta er.'),
        },
      ],
    },
  ].filter(section => isCustomerUser && section.visible);

  const showNotificationsTab = notificationSections.length > 0;
  const tabs = [
    t('tabs.profile', 'Profil'),
    ...(showNotificationsTab ? [t('tabs.notifications', 'Aviseringar')] : []),
    t('tabs.changePassword', 'Byt lösenord'),
  ];
  const changePasswordIndex = showNotificationsTab ? 2 : 1;

  return (
    <AppLayout title={t('pageTitle', 'Din profil')}>
      <div className="settings-container">
        <Tabs tabs={tabs} selected={value} onChange={handleChange} />
        <TabPanel value={value} index={0}>
          <div className="profile-wrapper">
            <UserDetailsForm
              isProfile
              user={userData}
              setParentUser={setUserData}
            />
          </div>
        </TabPanel>
        {showNotificationsTab && (
          <TabPanel value={value} index={1}>
            <div className="notification-settings-wrapper">
              {notificationSections.map(section => (
                <section className="notification-section" key={section.key}>
                  <div className="notification-section-intro">
                    <p>{section.title}</p>
                    <span>{section.description}</span>
                  </div>
                  <div className="notification-section-options">
                    {section.rows.map(row => {
                      const checked = notificationPreferences[row.key] ?? row.defaultChecked;
                      return (
                        <div className="notification-option" key={row.key}>
                          <div>
                            <p>{row.title}</p>
                            <span>{row.description}</span>
                          </div>
                          <CustomToggle checked={checked} onChange={() => appState.updateUserNotificationPreferences(row.key, !checked)} />
                        </div>
                      );
                    })}
                    {section.extra}
                  </div>
                </section>
              ))}
            </div>
          </TabPanel>
        )}
        <TabPanel value={value} index={changePasswordIndex}>
          <div className="change-password-wrapper">
            <AppChangePassword
              email={_.get(userData, 'email', 'Loading email...')}
              orgSlug={appState._getCurrentOrgUrl()}
              changePassword
            />
          </div>
        </TabPanel>
      </div>
    </AppLayout>
  );
}
